import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiGet } from 'services';
import { hasModule } from 'helpers/profile';
import { ArrayHelpers, Loading, Paging, SystemText } from '@salesperformanceint/e4shared';

import { NextReview } from './components/nextReview';
import { StaffReviews } from './components/staffReviews';
import { CoachReviews } from './components/coachReviews';
import { Objectives } from './components/objectives';
import { StaffObjectives } from './components/staffObjectives';
import { FeedbackObjectives } from './components/feedbackObjectives';
import { SharedObjectives } from './components/sharedObjectives';
import { YourBlogs } from './components/yourBlogs';
import { Answers } from './components/answers';

import { setHomeWhatsHappening } from 'actions/dashboard';

import './whatsHappening.css';
import WidgetBase from 'views/myDashboard/components/widgets/_base';
import Unreviewed from './components/unreviewed';
import Pathway from './components/pathway';

export const WhatsHappeningInfo = ({ item, user, data }) => {
    const dispatch = useDispatch();
    const loaded = React.useRef(null);
    const [pagedData, setPagedData] = React.useState([]);
    const [items, setItems] = React.useState([]);
    const [page, setPage] = React.useState(1);
    const [pageSize, setPageSize] = React.useState(6);
    const rowHeight = 100;
    const extrasHeight = 50;

    React.useEffect(() => {
        const temp = [];
        data.unreviewed?.length && temp.push(<Unreviewed data={data.unreviewed} setShow={() => setShow('unreviewed')} />);

        !user.zero && data.nextReview && temp.push(<NextReview data={data.nextReview} />);

        data.staffReviews?.length &&
            temp.push(<StaffReviews data={data.staffReviews} setShow={() => setShow('staff-reviews')} />);

        data.coachReviews?.length &&
            temp.push(<CoachReviews data={data.coachReviews} setShow={() => setShow('coach-reviews')} />);

        hasModule(user, 'objectives') &&
            data.objectives?.length &&
            temp.push(<Objectives data={data.objectives} setShow={() => setShow('objectives')} />);

        hasModule(user, 'pathways') &&
            data.pathways?.length &&
            temp.push(<Pathway data={data.pathways} setShow={() => setShow('pathways')} />);

        hasModule(user, 'objectives') &&
            data.staffObjectives?.length &&
            temp.push(<StaffObjectives data={data.staffObjectives} setShow={() => setShow('staff-objectives')} />);

        hasModule(user, 'objectives') &&
            data.feedbackObjectives?.length &&
            temp.push(<FeedbackObjectives data={data.feedbackObjectives} setShow={() => setShow('feedback-objectives')} />);

        hasModule(user, 'objectives') &&
            data.sharedObjectives?.length &&
            temp.push(<SharedObjectives data={data.sharedObjectives} setShow={() => setShow('shared-objectives')} />);

        hasModule(user, 'blogs') &&
            data.yourBlogs?.length &&
            temp.push(<YourBlogs data={data.yourBlogs} setShow={() => setShow('your-blogs')} />);

        hasModule(user, 'knowledge') &&
            data.answers?.length &&
            temp.push(<Answers data={data.answers} setShow={() => setShow('answers')} />);

        setItems(temp);
    }, [page]);

    React.useEffect(() => {
        setPagedData(ArrayHelpers.getPage(items, page, pageSize));
    }, [items]);

    React.useEffect(() => {
        const pageSizeGenerator = () => {
            const thisHeight = 110 * item?.h - 50;
            let result = Math.ceil((thisHeight - extrasHeight) / rowHeight, 0);
            if (result < items.length) result--;
            return result < 1 ? 1 : result;
        };

        const ps = pageSizeGenerator();
        setPagedData(ArrayHelpers.getPage(items, 1, ps));
        setPage(1);
        setPageSize(ps);
    }, [loaded.current, data, item?.h, items.length]);

    const setShow = show => dispatch(setHomeWhatsHappening({ show, data }));

    return (
        <div ref={loaded}>
            <div id="whats-happening">
                {pagedData.map((item, i) => (
                    <div key={`item-${i}`}>{item}</div>
                ))}
            </div>
            {items.length > pageSize && (
                <div className="text-center mt10">
                    <Paging
                        page={page}
                        narrow={item?.w < 3}
                        pageSize={pageSize}
                        recordCount={items.length}
                        goToPage={setPage}
                    />
                </div>
            )}
        </div>
    );
};

export const WhatsHappening = ({ item }) => {
    const [loading, setLoading] = React.useState(true);
    const [data, setData] = React.useState(null);

    const user = useSelector(state => state.user.user);

    React.useEffect(() => {
        setLoading(true);
        apiGet('whats-happening', user.guid, response => {
            if (!hasModule(user, 'objectives')) response.objectives = null;
            if (!hasModule(user, 'objectives')) response.staffObjectives = null;
            if (!hasModule(user, 'objectives')) response.feedbackObjectives = null;
            if (!hasModule(user, 'objectives')) response.sharedObjectives = null;
            if (!hasModule(user, 'pathways')) response.pathways = null;
            if (!hasModule(user, 'blogs')) response.yourBlogs = null;
            if (!hasModule(user, 'knowledge')) response.answers = null;
            setData(response);
            setLoading(false);
        });
    }, [user.guid]);

    const noData =
        data &&
        !data.nextReview &&
        (!data.unreviewed || data.unreviewed.length === 0) &&
        (!data.staffReviews || data.staffReviews.length === 0) &&
        (!data.coachReviews || data.coachReviews.length === 0) &&
        (!data.objectives || data.objectives.length === 0) &&
        (!data.pathways || data.pathways.length === 0) &&
        (!data.staffObjectives || data.staffObjectives.length === 0) &&
        (!data.feedbackObjectives || data.feedbackObjectives.length === 0) &&
        (!data.sharedObjectives || data.sharedObjectives.length === 0) &&
        (!data.answers || data.answers.length === 0) &&
        (!data.yourBlogs || data.yourBlogs.length === 0);

    const chart = (
        <div>
            {loading ? (
                <Loading />
            ) : noData ? (
                <div>
                    <i className="fa fa-thumbs-up text-success mr10 text-lg" />
                    {SystemText('Home/Widgets/WhatsHappening/CaughtUp')}
                </div>
            ) : (
                <WhatsHappeningInfo item={item} user={user} data={data} />
            )}
        </div>
    );

    return item ? (
        <WidgetBase
            filters={null}
            chart={chart}
            item={item}
            id={'whats-happening'}
            help={null}
            hideConfig
            extra={null}
            loading={loading}
        />
    ) : (
        chart
    );
};

export default WhatsHappening;
